import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useDashboardEvents } from "@/hooks/useEvents";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, ExternalLink, Loader2, Image as Video } from "lucide-react";
import { format, isPast, isToday, isTomorrow, isThisWeek, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { getVideoEmbedUrl } from "@/hooks/useAnnouncementAttachments";
import { getStorageUrl } from "@/lib/storage";
import { formatEventTime, getTimezoneLabel } from "@/lib/eventTimezone";
export default function Events() {
  const { data: events, isLoading } = useDashboardEvents();

  const getEventTimeLabel = (eventDate: string, timezone: string) => {
    // Parse and convert to event's timezone for accurate date comparisons
    const utcDate = parseISO(eventDate);
    const zonedDate = toZonedTime(utcDate, timezone);
    if (isPast(zonedDate) && !isToday(zonedDate)) return { label: "Past", variant: "secondary" as const };
    if (isToday(zonedDate)) return { label: "Today", variant: "default" as const };
    if (isTomorrow(zonedDate)) return { label: "Tomorrow", variant: "default" as const };
    if (isThisWeek(zonedDate)) return { label: "This Week", variant: "outline" as const };
    return null;
  };

  // Use timezone-aware date parsing for filtering
  const getZonedDate = (e: { event_date: string; timezone?: string | null }) => {
    const tz = e.timezone || "America/Jamaica";
    return toZonedTime(parseISO(e.event_date), tz);
  };

  const upcomingEvents = events?.filter((e) => {
    const zonedDate = getZonedDate(e);
    return !isPast(zonedDate) || isToday(zonedDate);
  }) || [];
  
  const pastEvents = events?.filter((e) => {
    const zonedDate = getZonedDate(e);
    return isPast(zonedDate) && !isToday(zonedDate);
  }) || [];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const EventCard = ({ event }: { event: (typeof events)[number] }) => {
    const eventTimezone = event.timezone || "America/Jamaica";
    const timeLabel = getEventTimeLabel(event.event_date, eventTimezone);
    const images = event.attachments?.filter((a) => a.attachment_type === "image") || [];
    const videos = event.attachments?.filter((a) => a.attachment_type === "video") || [];
    
    // Find featured image, or use first image as fallback
    const featuredImage = images.find((img) => img.is_featured) || images[0];
    const galleryImages = images.filter((img) => img.id !== featuredImage?.id);

    return (
      <Card className="overflow-hidden">
        {featuredImage && (
          <div className="relative aspect-video">
            <img
              src={getStorageUrl("attachments", featuredImage.url) || featuredImage.url}
              alt={event.title}
              className="w-full h-full object-cover"
            />
            {galleryImages.length > 0 && (
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                +{galleryImages.length} more
              </div>
            )}
          </div>
        )}
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <CardTitle className="text-lg">{event.title}</CardTitle>
                {timeLabel && (
                  <Badge variant={timeLabel.variant}>{timeLabel.label}</Badge>
                )}
              </div>
              <CardDescription className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatEventTime(event.event_date, eventTimezone, "EEEE, MMMM d, yyyy 'at' h:mm a")}
                <span className="text-xs ml-1">
                  ({getTimezoneLabel(eventTimezone)})
                </span>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {event.description && (
            <p className="text-sm text-muted-foreground">{event.description}</p>
          )}

          {/* Gallery images displayed below the event details */}
          {galleryImages.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {galleryImages.map((img) => (
                <div key={img.id} className="aspect-square rounded-md overflow-hidden">
                  <img
                    src={getStorageUrl("attachments", img.url) || img.url}
                    alt=""
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>
              ))}
            </div>
          )}

          {videos.length > 0 && (
            <div className="space-y-2">
              {videos.map((video) => {
                const embedUrl = getVideoEmbedUrl(video.url);
                if (embedUrl) {
                  return (
                    <div key={video.id} className="aspect-video rounded-md overflow-hidden">
                      <iframe
                        src={embedUrl}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                }
                return (
                  <a
                    key={video.id}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <Video className="h-4 w-4" />
                    Watch Video
                  </a>
                );
              })}
            </div>
          )}

          {event.registration_link && (
            <Button asChild className="w-full">
              <a
                href={event.registration_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Register for Event
              </a>
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Events</h1>
          <p className="text-muted-foreground mt-1">
            Discover upcoming events and opportunities
          </p>
        </div>

        {upcomingEvents.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Upcoming Events</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {pastEvents.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">Past Events</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {pastEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {(!events || events.length === 0) && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Calendar className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-1">No Events Yet</h3>
              <p className="text-muted-foreground text-center">
                Check back later for upcoming events and opportunities.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
